// operationsUI.jsx
//
// Shared design system for:
// - Operations Dashboard
// - Operations Camp Details
// - Operations Navigation / Header
//
// Goals:
// - Consistent visual language across operations screens
// - Healthcare / enterprise-friendly UI
// - Accessible reusable primitives
// - No external icon dependency
// - Responsive desktop + tablet + mobile
//
// Brand: #11a8a4
//
// Status semantics:
//   Green  = Completed
//   Amber  = Pending / In Progress
//   Red    = Overdue
//   Teal   = Ready
//   Slate  = Scheduled

import React from 'react';

/* ============================================================================
 * Design Tokens
 * ========================================================================== */

export const BRAND = {
  teal: '#11a8a4',
  tealDark: '#0b7d7a',
  tealDarker: '#0a6360',

  tealLight: '#e6f7f6',
  tealSoft: '#ccefed',

  slate: '#64748b',

  green: '#16a34a',
  greenLight: '#e7f7ed',

  amber: '#f59e0b',
  amberLight: '#fef3e2',

  red: '#ef4444',
  redLight: '#fdecec',

  white: '#ffffff',
  black: '#0f172a',
};

/* ============================================================================
 * Status Configuration
 * ========================================================================== */

export const STATUS_COLORS = {
  scheduled: '#94a3b8',
  ready: BRAND.teal,
  in_progress: BRAND.amber,
  completed: BRAND.green,
  overdue: BRAND.red,
};

export const STATUS_STYLES = {
  scheduled:
    'bg-slate-100 text-slate-700 border-slate-200',

  ready:
    'bg-[#e6f7f6] text-[#0b7d7a] border-[#b7e8e5]',

  in_progress:
    'bg-amber-50 text-amber-700 border-amber-200',

  completed:
    'bg-green-50 text-green-700 border-green-200',

  overdue:
    'bg-red-50 text-red-700 border-red-200',
};

export const CAMP_STATUS_BADGE = {
  SCHEDULED:
    'bg-slate-100 text-slate-700 border border-slate-200',

  READY:
    'bg-[#e6f7f6] text-[#0b7d7a] border border-[#b7e8e5]',

  IN_PROGRESS:
    'bg-amber-50 text-amber-700 border border-amber-200',

  COMPLETED:
    'bg-green-50 text-green-700 border border-green-200',

  OVERDUE:
    'bg-red-50 text-red-700 border border-red-200',
};

export const STATUS_LABELS = {
  SCHEDULED: 'Scheduled',
  READY: 'Ready',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  OVERDUE: 'Overdue',
};

/* ============================================================================
 * Shared Class Names
 * ========================================================================== */

export const UI_CLASSES = {
  card:
    'bg-white border border-slate-200 rounded-2xl shadow-sm',

  cardHover:
    'bg-white border border-slate-200 rounded-2xl shadow-sm transition-all duration-200 hover:border-[#b7e8e5] hover:shadow-md',

  sectionTitle:
    'text-sm font-semibold text-slate-800',

  muted:
    'text-sm text-slate-500',

  primaryButton:
    'inline-flex items-center justify-center gap-2 rounded-xl bg-[#11a8a4] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:bg-[#0b7d7a] hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#11a8a4] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',

  secondaryButton:
    'inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition-all duration-200 hover:bg-slate-50 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#11a8a4] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',

  dangerButton:
    'inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 shadow-sm transition-all duration-200 hover:border-red-200 hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',

  iconButton:
    'inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition-all duration-200 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#11a8a4] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',

  input:
    'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition-all placeholder:text-slate-400 focus:border-[#11a8a4] focus:ring-2 focus:ring-[#11a8a4]/10',

  select:
    'w-full appearance-none rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition-all focus:border-[#11a8a4] focus:ring-2 focus:ring-[#11a8a4]/10',

  page:
    'min-h-screen bg-slate-50',

  container:
    'mx-auto w-full max-w-[1600px] px-4 py-5 sm:px-6 lg:px-8',
};

/* ============================================================================
 * SVG Icon Base
 * ========================================================================== */

const base = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  viewBox: '0 0 24 24',
  'aria-hidden': true,
};

/* ============================================================================
 * Icons
 * ========================================================================== */

export const IconPulse = (props) => (
  <svg {...base} {...props}>
    <path d="M3 12h4l2-8 4 16 3-8h5" />
  </svg>
);

export const IconUsers = (props) => (
  <svg {...base} {...props}>
    <path d="M17 20v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 20v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

export const IconClipboard = (props) => (
  <svg {...base} {...props}>
    <rect x="6" y="4" width="12" height="17" rx="2" />
    <path d="M9 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1" />
    <path d="M9 11h6" />
    <path d="M9 15h6" />
  </svg>
);

export const IconCheckCircle = (props) => (
  <svg {...base} {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 12.5l2.3 2.3L15.8 9" />
  </svg>
);

export const IconClock = (props) => (
  <svg {...base} {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3.2 2" />
  </svg>
);

export const IconMapPin = (props) => (
  <svg {...base} {...props}>
    <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

export const IconCalendar = (props) => (
  <svg {...base} {...props}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M16 3v4" />
    <path d="M8 3v4" />
    <path d="M3 10h18" />
  </svg>
);

export const IconChevronDown = (props) => (
  <svg {...base} {...props}>
    <path d="M6 9l6 6 6-6" />
  </svg>
);

export const IconChevronRight = (props) => (
  <svg {...base} {...props}>
    <path d="M9 18l6-6-6-6" />
  </svg>
);

export const IconChevronLeft = (props) => (
  <svg {...base} {...props}>
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

export const IconLogOut = (props) => (
  <svg {...base} {...props}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="M16 17l5-5-5-5" />
    <path d="M21 12H9" />
  </svg>
);

export const IconRefreshCw = (props) => (
  <svg {...base} {...props}>
    <path d="M23 4v6h-6" />
    <path d="M1 20v-6h6" />
    <path d="M3.5 9a9 9 0 0 1 14.9-3.4L23 10" />
    <path d="M1 14l4.6 4.4A9 9 0 0 0 20.5 15" />
  </svg>
);

export const IconArrowLeft = (props) => (
  <svg {...base} {...props}>
    <path d="M19 12H5" />
    <path d="M12 19l-7-7 7-7" />
  </svg>
);

export const IconArrowRight = (props) => (
  <svg {...base} {...props}>
    <path d="M5 12h14" />
    <path d="M12 5l7 7-7 7" />
  </svg>
);

export const IconBuilding = (props) => (
  <svg {...base} {...props}>
    <rect x="4" y="3" width="16" height="18" rx="1" />
    <path d="M9 8h1" />
    <path d="M14 8h1" />
    <path d="M9 12h1" />
    <path d="M14 12h1" />
    <path d="M9 16h1" />
    <path d="M14 16h1" />
  </svg>
);

export const IconStethoscope = (props) => (
  <svg {...base} {...props}>
    <path d="M5 3v6a4 4 0 0 0 4 4h0a4 4 0 0 0 4-4V3" />
    <path d="M9 13v2a6 6 0 0 0 6 6h0a6 6 0 0 0 6-6v-1" />
    <circle cx="20" cy="8" r="2" />
  </svg>
);

export const IconAlertTriangle = (props) => (
  <svg {...base} {...props}>
    <path d="M12 3 1 21h22L12 3Z" />
    <path d="M12 10v5" />
    <path d="M12 18h.01" />
  </svg>
);

export const IconInfo = (props) => (
  <svg {...base} {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <path d="M12 8h.01" />
  </svg>
);

export const IconSearch = (props) => (
  <svg {...base} {...props}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-4-4" />
  </svg>
);

export const IconFilter = (props) => (
  <svg {...base} {...props}>
    <path d="M4 6h16" />
    <path d="M7 12h10" />
    <path d="M10 18h4" />
  </svg>
);

export const IconX = (props) => (
  <svg {...base} {...props}>
    <path d="M6 6l12 12" />
    <path d="M18 6 6 18" />
  </svg>
);

export const IconCheck = (props) => (
  <svg {...base} {...props}>
    <path d="m5 12 4 4L19 6" />
  </svg>
);

/* ============================================================================
 * Additional Navigation / Dashboard Icons
 * ========================================================================== */

export const IconHome = (props) => (
  <svg {...base} {...props}>
    <path d="m3 10 9-7 9 7" />
    <path d="M5 9v11h14V9" />
    <path d="M9 20v-6h6v6" />
  </svg>
);

export const IconMenu = (props) => (
  <svg {...base} {...props}>
    <path d="M4 6h16" />
    <path d="M4 12h16" />
    <path d="M4 18h16" />
  </svg>
);

export const IconUser = (props) => (
  <svg {...base} {...props}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21a8 8 0 0 1 16 0" />
  </svg>
);

export const IconSettings = (props) => (
  <svg {...base} {...props}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-1.8 1.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-2.6V20a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1-1.8-1.8.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.6-1H6v-2.6h.2a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9l-.1-.1 1.8-1.8.1.1a1.7 1.7 0 0 0 1.9.3 1.7 1.7 0 0 0 1-1.6V4h2.6v.2a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1 1.8 1.8-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v2.6H21a1.7 1.7 0 0 0-1.6 1Z" />
  </svg>
);

export const IconExternalLink = (props) => (
  <svg {...base} {...props}>
    <path d="M14 5h5v5" />
    <path d="M10 14 19 5" />
    <path d="M19 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h4" />
  </svg>
);

export const IconMoreVertical = (props) => (
  <svg {...base} {...props}>
    <circle cx="12" cy="5" r="1" fill="currentColor" />
    <circle cx="12" cy="12" r="1" fill="currentColor" />
    <circle cx="12" cy="19" r="1" fill="currentColor" />
  </svg>
);

export const IconDownload = (props) => (
  <svg {...base} {...props}>
    <path d="M12 3v12" />
    <path d="m7 10 5 5 5-5" />
    <path d="M5 21h14" />
  </svg>
);

export const IconPlus = (props) => (
  <svg {...base} {...props}>
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

/* ============================================================================
 * Utility Functions
 * ========================================================================== */

export function clampPercentage(value) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return 0;
  }

  return Math.max(0, Math.min(100, number));
}

export function normalizeStatus(status) {
  return String(status || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
}

export function formatStatusLabel(status) {
  const normalized = normalizeStatus(status);

  if (STATUS_LABELS[normalized]) {
    return STATUS_LABELS[normalized];
  }

  if (!normalized) {
    return 'Unknown';
  }

  return normalized
    .toLowerCase()
    .split('_')
    .map(
      (word) =>
        word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(' ');
}

export function getStatusColor(status) {
  const normalized = normalizeStatus(status);

  return (
    STATUS_COLORS[normalized.toLowerCase()] ||
    BRAND.slate
  );
}

/* ============================================================================
 * Card
 * ========================================================================== */

export function Card({
  children,
  className = '',
  hover = false,
  ...props
}) {
  return (
    <div
      className={`${
        hover
          ? UI_CLASSES.cardHover
          : UI_CLASSES.card
      } ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

/* ============================================================================
 * Section Header
 * ========================================================================== */

export function SectionHeader({
  title,
  description,
  icon: Icon,
  action,
  className = '',
}) {
  return (
    <div
      className={`flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${className}`}
    >
      <div className="flex min-w-0 items-start gap-3">
        {Icon && (
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#e6f7f6] text-[#0b7d7a]">
            <Icon className="h-4 w-4" />
          </div>
        )}

        <div className="min-w-0">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-800">
            {title}
          </h2>

          {description && (
            <p className="mt-0.5 text-xs text-slate-500">
              {description}
            </p>
          )}
        </div>
      </div>

      {action && (
        <div className="shrink-0">
          {action}
        </div>
      )}
    </div>
  );
}

/* ============================================================================
 * Page Header
 * ========================================================================== */

export function PageHeader({
  title,
  description,
  icon: Icon = IconPulse,
  backButton,
  action,
  breadcrumb,
  className = '',
}) {
  return (
    <div className={`mb-6 ${className}`}>
      {breadcrumb && (
        <div className="mb-3">
          {breadcrumb}
        </div>
      )}

      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          {backButton && (
            <div className="shrink-0">
              {backButton}
            </div>
          )}

          <div className="flex min-w-0 items-start gap-3">
            {Icon && (
              <div className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#e6f7f6] text-[#0b7d7a] sm:flex">
                <Icon className="h-5 w-5" />
              </div>
            )}

            <div className="min-w-0">
              <h1 className="truncate text-xl font-bold text-slate-900 sm:text-2xl">
                {title}
              </h1>

              {description && (
                <p className="mt-1 max-w-3xl text-sm text-slate-500">
                  {description}
                </p>
              )}
            </div>
          </div>
        </div>

        {action && (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {action}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================================
 * Status Badge
 * ========================================================================== */

export function StatusBadge({
  status,
  size = 'sm',
  dot = true,
  className = '',
}) {
  const normalized = normalizeStatus(status);

  const style =
    CAMP_STATUS_BADGE[normalized] ||
    'bg-slate-100 text-slate-700 border border-slate-200';

  const color = getStatusColor(status);

  const sizeClass =
    size === 'md'
      ? 'px-3 py-1.5 text-sm'
      : 'px-2.5 py-1 text-xs';

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border font-semibold ${sizeClass} ${style} ${className}`}
    >
      {dot && (
        <span
          className="h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: color }}
        />
      )}

      {formatStatusLabel(status)}
    </span>
  );
}

/* ============================================================================
 * Status Indicator
 * ========================================================================== */

export function StatusIndicator({
  status,
  showLabel = true,
  className = '',
}) {
  const color = getStatusColor(status);

  return (
    <span
      className={`inline-flex items-center gap-2 ${className}`}
    >
      <span
        className="h-2 w-2 rounded-full"
        style={{
          backgroundColor: color,
        }}
      />

      {showLabel && (
        <span className="text-xs font-medium text-slate-600">
          {formatStatusLabel(status)}
        </span>
      )}
    </span>
  );
}

/* ============================================================================
 * Radial Progress
 * ========================================================================== */

export function RadialProgress({
  percentage,
  size = 56,
  stroke = 6,
  color = BRAND.teal,
  trackColor = BRAND.tealLight,
  showLabel = true,
  labelClassName = 'text-xs font-bold',
  labelSuffix = '%',
}) {
  const value = Math.round(
    clampPercentage(percentage)
  );

  const radius = Math.max(
    1,
    (size - stroke) / 2
  );

  const circumference =
    2 * Math.PI * radius;

  const offset =
    circumference -
    (value / 100) * circumference;

  return (
    <div
      className="relative inline-flex shrink-0 items-center justify-center"
      style={{
        width: size,
        height: size,
      }}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={value}
      aria-label={`${value}% complete`}
    >
      <svg
        width={size}
        height={size}
        className="-rotate-90"
        aria-hidden="true"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={trackColor}
          strokeWidth={stroke}
          fill="none"
        />

        {value > 0 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={color}
            strokeWidth={stroke}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-700 ease-out"
          />
        )}
      </svg>

      {showLabel && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            className={labelClassName}
            style={{ color }}
          >
            {value}
            {labelSuffix}
          </span>
        </div>
      )}
    </div>
  );
}

/* ============================================================================
 * Status Donut
 * ========================================================================== */

const STATUS_ITEMS = [
  {
    key: 'scheduled',
    label: 'Scheduled',
  },
  {
    key: 'ready',
    label: 'Ready',
  },
  {
    key: 'in_progress',
    label: 'In Progress',
  },
  {
    key: 'completed',
    label: 'Completed',
  },
  {
    key: 'overdue',
    label: 'Overdue',
  },
];

export function StatusDonut({
  campStatus,
  size = 140,
  stroke = 16,
}) {
  const values = STATUS_ITEMS.map(
    (item) =>
      Number(campStatus?.[item.key]) || 0
  );

  const total = values.reduce(
    (sum, value) => sum + value,
    0
  );

  const radius = Math.max(
    1,
    (size - stroke) / 2
  );

  const circumference =
    2 * Math.PI * radius;

  let cumulative = 0;

  const segments = STATUS_ITEMS.map(
    (item, index) => {
      const value = values[index];

      const fraction =
        total > 0 ? value / total : 0;

      const dash =
        fraction * circumference;

      const rotationOffset =
        total > 0
          ? (cumulative / total) *
            circumference
          : 0;

      cumulative += value;

      return {
        ...item,
        value,
        dash,
        rotationOffset,
        color: STATUS_COLORS[item.key],
      };
    }
  );

  return (
    <div className="flex flex-col items-center gap-6 sm:flex-row">
      <div
        className="relative shrink-0"
        style={{
          width: size,
          height: size,
        }}
        role="img"
        aria-label={`${total} total camps`}
      >
        <svg
          width={size}
          height={size}
          className="-rotate-90"
          aria-hidden="true"
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#f1f5f9"
            strokeWidth={stroke}
            fill="none"
          />

          {total > 0 &&
            segments.map((segment) => {
              if (segment.value === 0) {
                return null;
              }

              return (
                <circle
                  key={segment.key}
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  stroke={segment.color}
                  strokeWidth={stroke}
                  fill="none"
                  strokeDasharray={`${segment.dash} ${
                    circumference -
                    segment.dash
                  }`}
                  strokeDashoffset={
                    -segment.rotationOffset
                  }
                  strokeLinecap="butt"
                  className="transition-all duration-500"
                />
              );
            })}
        </svg>

        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold leading-none text-slate-900 tabular-nums">
            {total}
          </span>

          <span className="mt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            Camps
          </span>
        </div>
      </div>

      <div className="w-full min-w-0 space-y-2.5 sm:w-auto sm:min-w-[190px]">
        {segments.map((segment) => (
          <div
            key={segment.key}
            className="flex items-center gap-2.5 text-sm"
          >
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{
                backgroundColor: segment.color,
              }}
            />

            <span className="min-w-0 flex-1 truncate text-slate-600">
              {segment.label}
            </span>

            <span className="font-semibold tabular-nums text-slate-900">
              {segment.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================================
 * Progress Bar
 * ========================================================================== */

export function ProgressBar({
  percentage,
  color = BRAND.teal,
  trackColor = '#f1f5f9',
  height = 'h-2',
  showLabel = false,
  className = '',
}) {
  const value = Math.round(
    clampPercentage(percentage)
  );

  return (
    <div className={className}>
      {showLabel && (
        <div className="mb-1.5 flex items-center justify-between">
          <span className="text-xs font-medium text-slate-500">
            Progress
          </span>

          <span className="text-xs font-semibold text-slate-700">
            {value}%
          </span>
        </div>
      )}

      <div
        className={`w-full overflow-hidden rounded-full ${height}`}
        style={{
          backgroundColor: trackColor,
        }}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={value}
      >
        <div
          className={`${height} rounded-full transition-all duration-500 ease-out`}
          style={{
            width: `${value}%`,
            backgroundColor: color,
          }}
        />
      </div>
    </div>
  );
}

/* ============================================================================
 * Metric Card
 * ========================================================================== */

export function MetricCard({
  label,
  value,
  icon: Icon,
  accent = BRAND.teal,
  description,
  className = '',
}) {
  return (
    <Card
      className={`group p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${className}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">
            {label}
          </p>

          <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
            {value}
          </p>

          {description && (
            <p className="mt-1 text-xs text-slate-500">
              {description}
            </p>
          )}
        </div>

        {Icon && (
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-transform duration-200 group-hover:scale-105"
            style={{
              backgroundColor: `${accent}18`,
              color: accent,
            }}
          >
            <Icon className="h-5 w-5" />
          </div>
        )}
      </div>
    </Card>
  );
}

/* ============================================================================
 * Status Count
 * ========================================================================== */

export function StatusCount({
  status,
  count = 0,
  className = '',
}) {
  const color = getStatusColor(status);

  return (
    <div
      className={`flex items-center gap-2 ${className}`}
    >
      <span
        className="h-2 w-2 rounded-full"
        style={{
          backgroundColor: color,
        }}
      />

      <span className="text-sm text-slate-500">
        {formatStatusLabel(status)}
      </span>

      <span className="ml-auto text-sm font-bold tabular-nums text-slate-900">
        {count}
      </span>
    </div>
  );
}

/* ============================================================================
 * Icon Button
 * ========================================================================== */

export function IconButton({
  icon: Icon,
  label,
  className = '',
  size = 'md',
  ...props
}) {
  const sizeClass =
    size === 'sm'
      ? 'h-8 w-8'
      : size === 'lg'
        ? 'h-11 w-11'
        : 'h-9 w-9';

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={`${UI_CLASSES.iconButton} ${sizeClass} ${className}`}
      {...props}
    >
      {Icon && <Icon className="h-4 w-4" />}
    </button>
  );
}

/* ============================================================================
 * Logout Button
 *
 * Usage:
 *
 * <LogoutButton onLogout={handleLogout} />
 *
 * handleLogout should clear authentication and navigate to login.
 * ========================================================================== */

export function LogoutButton({
  onLogout,
  label = 'Logout',
  compact = false,
  className = '',
}) {
  return (
    <button
      type="button"
      onClick={onLogout}
      className={`${
        compact
          ? 'h-9 w-9 justify-center px-0'
          : ''
      } ${UI_CLASSES.dangerButton} ${className}`}
      title={label}
      aria-label={label}
    >
      <IconLogOut className="h-4 w-4" />

      {!compact && (
        <span>{label}</span>
      )}
    </button>
  );
}

/* ============================================================================
 * Search Input
 * ========================================================================== */

export function SearchInput({
  value,
  onChange,
  placeholder = 'Search...',
  className = '',
  onClear,
}) {
  return (
    <div className={`relative ${className}`}>
      <IconSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

      <input
        type="search"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`${UI_CLASSES.input} pl-9 ${
          value ? 'pr-9' : ''
        }`}
        aria-label={placeholder}
      />

      {value && onClear && (
        <button
          type="button"
          onClick={onClear}
          className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          aria-label="Clear search"
        >
          <IconX className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

/* ============================================================================
 * Filter Select
 * ========================================================================== */

export function FilterSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  label,
  className = '',
}) {
  return (
    <div className={className}>
      {label && (
        <label className="mb-1.5 block text-xs font-semibold text-slate-600">
          {label}
        </label>
      )}

      <div className="relative">
        <select
          value={value}
          onChange={onChange}
          className={`${UI_CLASSES.select} pr-10`}
          aria-label={label || placeholder}
        >
          {placeholder && (
            <option value="">
              {placeholder}
            </option>
          )}

          {options.map((option) => {
            const item =
              typeof option === 'object'
                ? option
                : {
                    value: option,
                    label: option,
                  };

            return (
              <option
                key={item.value}
                value={item.value}
              >
                {item.label}
              </option>
            );
          })}
        </select>

        <IconChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      </div>
    </div>
  );
}

/* ============================================================================
 * Empty State
 * ========================================================================== */

export function EmptyState({
  title = 'Nothing found',
  description,
  icon: Icon = IconClipboard,
  action,
  className = '',
}) {
  return (
    <div
      className={`flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-12 text-center ${className}`}
    >
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-50 text-slate-400">
        <Icon className="h-5 w-5" />
      </div>

      <h3 className="text-sm font-semibold text-slate-800">
        {title}
      </h3>

      {description && (
        <p className="mt-1.5 max-w-md text-sm text-slate-500">
          {description}
        </p>
      )}

      {action && (
        <div className="mt-4">
          {action}
        </div>
      )}
    </div>
  );
}

/* ============================================================================
 * Error State
 * ========================================================================== */

export function ErrorState({
  title = 'Unable to load data',
  message,
  action,
  className = '',
}) {
  return (
    <div
      className={`rounded-2xl border border-red-200 bg-red-50 p-5 ${className}`}
      role="alert"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-red-100 text-red-600">
          <IconAlertTriangle className="h-4 w-4" />
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-red-800">
            {title}
          </h3>

          {message && (
            <p className="mt-1 text-sm leading-5 text-red-700">
              {message}
            </p>
          )}

          {action && (
            <div className="mt-3">
              {action}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
 * Loading State
 * ========================================================================== */

export function LoadingState({
  message = 'Loading...',
  className = '',
}) {
  return (
    <div
      className={`flex min-h-[240px] flex-col items-center justify-center ${className}`}
      role="status"
      aria-live="polite"
    >
      <div className="h-9 w-9 animate-spin rounded-full border-2 border-slate-200 border-t-[#11a8a4]" />

      <p className="mt-3 text-sm font-medium text-slate-500">
        {message}
      </p>
    </div>
  );
}

/* ============================================================================
 * Skeleton Block
 * ========================================================================== */

export function SkeletonBlock({
  className = '',
  rounded = 'rounded-xl',
}) {
  return (
    <div
      aria-hidden="true"
      className={`animate-pulse bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100 ${rounded} ${className}`}
    />
  );
}

/* ============================================================================
 * Skeleton Text
 * ========================================================================== */

export function SkeletonText({
  width = 'w-full',
  height = 'h-4',
  className = '',
}) {
  return (
    <SkeletonBlock
      className={`${width} ${height} ${className}`}
      rounded="rounded-md"
    />
  );
}

/* ============================================================================
 * Divider
 * ========================================================================== */

export function Divider({
  className = '',
}) {
  return (
    <div
      className={`h-px w-full bg-slate-100 ${className}`}
      aria-hidden="true"
    />
  );
}

/* ============================================================================
 * Avatar
 * ========================================================================== */

export function Avatar({
  name,
  size = 'md',
  className = '',
}) {
  const initials =
    name
      ?.split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) =>
        part.charAt(0).toUpperCase()
      )
      .join('') || '?';

  const sizeClass =
    size === 'sm'
      ? 'h-8 w-8 text-xs'
      : size === 'lg'
        ? 'h-12 w-12 text-base'
        : 'h-10 w-10 text-sm';

  return (
    <div
      className={`flex shrink-0 items-center justify-center rounded-full bg-[#e6f7f6] font-semibold text-[#0b7d7a] ${sizeClass} ${className}`}
      aria-label={name || 'Unknown user'}
      title={name || 'Unknown user'}
    >
      {initials}
    </div>
  );
}

/* ============================================================================
 * User Menu
 *
 * This is intentionally presentational.
 * The parent component controls logout/navigation.
 * ========================================================================== */

export function UserMenu({
  userName = 'User',
  userRole = 'Operations',
  onLogout,
  className = '',
}) {
  return (
    <div
      className={`flex items-center gap-3 ${className}`}
    >
      <Avatar
        name={userName}
        size="sm"
      />

      <div className="hidden min-w-0 sm:block">
        <p className="max-w-[160px] truncate text-sm font-semibold text-slate-800">
          {userName}
        </p>

        <p className="text-xs text-slate-500">
          {userRole}
        </p>
      </div>

      {onLogout && (
        <LogoutButton
          onLogout={onLogout}
          compact
        />
      )}
    </div>
  );
}

/* ============================================================================
 * Breadcrumb
 * ========================================================================== */

export function Breadcrumb({
  items = [],
  onNavigate,
  className = '',
}) {
  return (
    <nav
      aria-label="Breadcrumb"
      className={className}
    >
      <ol className="flex flex-wrap items-center gap-1.5 text-xs">
        {items.map((item, index) => {
          const isLast =
            index === items.length - 1;

          return (
            <React.Fragment
              key={`${item.label}-${index}`}
            >
              <li>
                {isLast || !item.onClick ? (
                  <span
                    className={
                      isLast
                        ? 'font-semibold text-slate-700'
                        : 'text-slate-500'
                    }
                  >
                    {item.label}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() =>
                      onNavigate
                        ? onNavigate(item)
                        : item.onClick()
                    }
                    className="font-medium text-[#0b7d7a] hover:underline"
                  >
                    {item.label}
                  </button>
                )}
              </li>

              {!isLast && (
                <IconChevronRight className="h-3.5 w-3.5 text-slate-300" />
              )}
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
}